import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { hoursLabel, minutesLabel, minutesOfHhmm } from '../../hr-schedule-engine';

// Données d'entrée : les créneaux existants du jour ; résultat : la liste éditée (triée), chaque
// ligne pouvant porter `copyDays` = jours (1..7) où DUPLIQUER ce créneau (« mêmes horaires du
// lundi au vendredi » en deux clics). undefined = annulation ; liste vide = journée sans travail.
export interface SlotDialogData {
	title: string;			// « Marie Durand — Lundi »
	subtitle?: string;	// « Semaine 2 » (contexte cycle) ou date réelle
	slots: { start_min: number; end_min: number; break_min?: number }[];
	dayIdx?: number;		// 0..6 — jour édité (exclu des cibles de duplication)
	dayLabels?: string[];
	// Horaires d'OUVERTURE du service CE jour-là : ce que « Ajouter un créneau » propose.
	openings?: { start_min: number; end_min: number }[];
}

export interface SlotDialogRow {
	start_min: number;
	end_min: number;
	break_min: number;	// pause déduite du temps de travail (0 = aucune)
	copyDays: number[];	// jours ISO 1..7 cibles de duplication (vide = pas de copie)
}

@Component({
	selector: 'app-hr-slot-dialog',
	standalone: true,
	imports: [ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatTooltipModule],
	templateUrl: './slot-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './slot-dialog.component.scss',
})
export class SlotDialogComponent {

	public data: SlotDialogData = inject(MAT_DIALOG_DATA);
	private dialogRef = inject(MatDialogRef<SlotDialogComponent>);

	public rows: FormArray<FormGroup>;
	public copyDays: Set<number>[] = [];		// aligné sur rows : jours cibles de chaque créneau
	public errorMessage = '';
	// Initiales des jours pour les pastilles de duplication (L M M J V S D).
	public dayChips: { day: number; initial: string; full: string }[] = [];

	constructor() {
		this.rows = new FormArray<FormGroup>(
			(this.data.slots ?? [])
				.slice()
				.sort((a, b) => a.start_min - b.start_min)
				.map(s => this.newRow(minutesLabel(s.start_min), minutesLabel(s.end_min), s.break_min ?? 0))
		);
		this.copyDays = this.rows.controls.map(() => new Set<number>());
		const labels = this.data.dayLabels ?? [];
		this.dayChips = labels.map((full, i) => ({ day: i + 1, initial: full.charAt(0), full }))
			.filter(c => c.day !== (this.data.dayIdx ?? -1) + 1);
	}

	private newRow(start = '', end = '', breakMin = 0): FormGroup {
		return new FormGroup({
			start: new FormControl<string>(start),
			end: new FormControl<string>(end),
			break_min: new FormControl<number>(breakMin),
		});
	}

	// Durée de TRAVAIL de la ligne (pause déduite), « 7h30 » — affichée en bout de ligne.
	rowDurationLabel(i: number): string {
		const row = this.rows.controls[i];
		const start = minutesOfHhmm(row.get('start')!.value);
		const end = minutesOfHhmm(row.get('end')!.value);
		if (start === null || end === null || end <= start) return '';
		const worked = (end - start) - Math.max(0, +(row.get('break_min')!.value ?? 0));
		return worked > 0 ? hoursLabel(worked) : '—';
	}

	addRow(): void {
		const [start, end] = this.nextProposal();
		this.rows.push(this.newRow(start, end));
		this.copyDays.push(new Set<number>());
	}

	// Ce que « Ajouter un créneau » propose : la prochaine plage d'OUVERTURE du service ce jour-là,
	// pas des heures inventées. Sur un magasin ouvert 9h–12h / 14h–19h, le premier clic donne
	// 09:00–12:00 et le second 14:00–19:00 — c'est la journée type, on n'a plus qu'à valider.
	private nextProposal(): [string, string] {
		const starts = this.rows.controls.map(r => minutesOfHhmm(r.get('start')!.value));
		for (const o of this.data.openings ?? []) {
			if (!starts.includes(o.start_min)) return [minutesLabel(o.start_min), minutesLabel(o.end_min)];
		}
		// Service sans horaires d'ouverture saisis (ou toutes ses plages déjà posées) : à défaut,
		// l'après-midi type après un créneau du matin, sinon la matinée type.
		return this.rows.length === 1 ? ['14:00', '18:00'] : ['08:00', '12:00'];
	}

	removeRow(i: number): void {
		this.rows.removeAt(i);
		this.copyDays.splice(i, 1);
		this.errorMessage = '';
	}

	toggleCopyDay(i: number, day: number): void {
		if (this.copyDays[i].has(day)) this.copyDays[i].delete(day);
		else this.copyDays[i].add(day);
	}

	copyAllWeekdays(i: number): void {
		// raccourci « du lundi au vendredi » (le jour édité reste implicitement inclus)
		const target = this.dayChips.filter(c => c.day <= 5).map(c => c.day);
		const allOn = target.every(d => this.copyDays[i].has(d));
		for (const d of target) allOn ? this.copyDays[i].delete(d) : this.copyDays[i].add(d);
	}

	// Valide et rend la liste triée en minutes (+ jours de duplication) ; null si erreur.
	private buildResult(): SlotDialogRow[] | null {
		const out: SlotDialogRow[] = [];
		for (let i = 0; i < this.rows.controls.length; i++) {
			const row = this.rows.controls[i];
			const start = minutesOfHhmm(row.get('start')!.value);
			const end = minutesOfHhmm(row.get('end')!.value);
			if (start === null || end === null) { this.errorMessage = 'Heures incomplètes ou invalides.'; return null; }
			if (end <= start) { this.errorMessage = 'La fin doit être après le début (pas de créneau à cheval sur minuit).'; return null; }
			const breakMin = Math.max(0, Math.round(+(row.get('break_min')!.value ?? 0)));
			if (breakMin >= end - start) { this.errorMessage = 'La pause doit être plus courte que le créneau.'; return null; }
			out.push({ start_min: start, end_min: end, break_min: breakMin, copyDays: Array.from(this.copyDays[i] ?? []).sort((a, b) => a - b) });
		}
		out.sort((a, b) => a.start_min - b.start_min);
		for (let i = 1; i < out.length; i++) {
			if (out[i].start_min < out[i - 1].end_min) { this.errorMessage = 'Les créneaux se chevauchent.'; return null; }
		}
		return out;
	}

	onValidate(): void {
		const result = this.buildResult();
		if (result !== null) this.dialogRef.close(result);
	}

	onCancel(): void {
		this.dialogRef.close(undefined);
	}
}
